'use client';

import React, { useState, useEffect } from 'react';
import PhoneInput, { type Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import styles from './Step3ContactNew.module.css';

interface Step3ContactProps {
  formData: {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    company: string;
    kassasysteem?: string;
    kassaleverancier?: string;
    message: string;
  };
  onChange: (field: string, value: string) => void;
  errors: Record<string, string | undefined>;
  translations: any;
  locale?: string; // Add locale prop for country detection
  hideTitle?: boolean; // Option to hide title/subtitle when used standalone
  hideMessage?: boolean; // Option to hide message field
  showKassaFields?: boolean; // Option to show kassasysteem/kassaleverancier fields
}

export default function Step3Contact({ formData, onChange, errors, translations, locale, hideTitle = false, hideMessage = false, showKassaFields = false }: Step3ContactProps) {
  const t = translations.leadRegistration?.form || translations.demoRequest?.form || translations.form || {};

  // State for detected country (client-side only)
  const [detectedCountry, setDetectedCountry] = useState<Country | undefined>(undefined);

  // AVG-compliant country detection using multiple sources
  // Priority: 1) Browser language 2) Page locale 3) International
  useEffect(() => {
    // Client-side country detection based on browser language (AVG-proof, no cookies/tracking)
    const detectCountryFromBrowser = (): Country | undefined => {
      if (typeof window === 'undefined') return undefined;
      
      // Get browser language (e.g., 'nl-NL', 'de-DE', 'it-IT', 'en-US')
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (!browserLang) return undefined;

      // Extract country code from language tag
      // Format can be: 'nl', 'nl-NL', 'nl-BE', etc.
      const parts = browserLang.toLowerCase().split('-');
      
      // If format is 'nl-NL', use the country part (NL)
      if (parts.length > 1) {
        return parts[1].toUpperCase() as Country; // 'NL', 'DE', 'IT', etc.
      }
      
      // If format is just 'nl', map language to likely country
      const langToCountry: Record<string, Country | undefined> = {
        'nl': 'NL' as Country,
        'de': 'DE' as Country,
        'it': 'IT' as Country,
        'fr': 'FR' as Country,
        'es': 'ES' as Country,
        'pt': 'PT' as Country,
        'pl': 'PL' as Country,
        'en': undefined, // English = international (no default)
      };
      
      return langToCountry[parts[0]];
    };

    // Fallback to page locale if browser detection doesn't work
    const getDefaultCountryFromLocale = (): Country | undefined => {
      switch (locale) {
        case 'nl': return 'NL' as Country;
        case 'de': return 'DE' as Country;
        case 'en':
        default: return undefined; // International
      }
    };

    // Try browser detection first, then fallback to locale
    const country = detectCountryFromBrowser() || getDefaultCountryFromLocale();
    setDetectedCountry(country);
  }, [locale]);

  return (
    <div className={styles.container}>
      {!hideTitle && (
        <>
          <h2 className={styles.title}>{t.step3?.title || 'Hoe kunnen we u bereiken?'}</h2>
          <p className={styles.subtitle}>{t.step3?.subtitle || 'Vul uw contactgegevens in zodat we contact met u kunnen opnemen'}</p>
        </>
      )}
      
      <div className={styles.form}>
        {/* First Name & Last Name - Side by Side */}
        <div className={styles.row}>
          <div className={styles.fieldGroup}>
            <label htmlFor="firstname" className={styles.label}>
              {t.labels?.firstname || 'Voornaam'} <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="firstname"
              value={formData.firstname}
              onChange={(e) => onChange('firstname', e.target.value)}
              className={`${styles.input} ${errors.firstname ? styles.inputError : ''}`}
              placeholder={t.placeholders?.firstname || 'Jan'}
            />
            {errors.firstname && <span className={styles.errorText}>{errors.firstname}</span>}
          </div>
          
          <div className={styles.fieldGroup}>
            <label htmlFor="lastname" className={styles.label}>
              {t.labels?.lastname || 'Achternaam'} <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="lastname"
              value={formData.lastname}
              onChange={(e) => onChange('lastname', e.target.value)}
              className={`${styles.input} ${errors.lastname ? styles.inputError : ''}`}
              placeholder={t.placeholders?.lastname || 'Jansen'}
            />
            {errors.lastname && <span className={styles.errorText}>{errors.lastname}</span>}
          </div>
        </div>

        {/* Email & Phone - Side by Side */}
        <div className={styles.row}>
          <div className={styles.fieldGroup}>
            <label htmlFor="email" className={styles.label}>
              {t.labels?.email || 'E-mailadres'} <span className={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => onChange('email', e.target.value)}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              placeholder={t.placeholders?.email || 'jan@bedrijf.nl'}
            />
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </div>
          
          <div className={styles.fieldGroup}>
            <label htmlFor="phone" className={styles.label}>
              {t.labels?.phone || 'Telefoonnummer'} <span className={styles.optional}>({t.labels?.optional || 'optioneel'})</span>
            </label>
            <PhoneInput
              international
              defaultCountry={detectedCountry}
              value={formData.phone}
              onChange={(value) => onChange('phone', value || '')}
              className={`${styles.phoneInput} ${errors.phone ? styles.inputError : ''}`}
              placeholder="+31 6 12345678"
            />
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
          </div>
        </div>

        {/* Company Name - Full Width */}
        <div className={styles.fieldGroup}>
          <label htmlFor="company" className={styles.label}>
            {t.labels?.company || 'Bedrijfsnaam'} <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            id="company"
            value={formData.company}
            onChange={(e) => onChange('company', e.target.value)}
            className={`${styles.input} ${errors.company ? styles.inputError : ''}`}
            placeholder={t.placeholders?.company || 'Mijn Restaurant B.V.'}
          />
          {errors.company && <span className={styles.errorText}>{errors.company}</span>}
        </div>

        {/* Kassasysteem & Kassaleverancier - Side by Side (Optional, only for lead registration) */}
        {showKassaFields && (
          <div className={styles.row}>
            <div className={styles.fieldGroup}>
              <label htmlFor="kassasysteem" className={styles.label}>
                {t.labels?.kassasysteem || 'Kassasysteem'} <span className={styles.optional}>({t.labels?.optional || 'optioneel'})</span>
              </label>
              <input
                type="text"
                id="kassasysteem"
                value={formData.kassasysteem || ''}
                onChange={(e) => onChange('kassasysteem', e.target.value)}
                className={`${styles.input} ${errors.kassasysteem ? styles.inputError : ''}`}
                placeholder={t.placeholders?.kassasysteem || 'Bijv. Lightspeed'}
              />
              {errors.kassasysteem && <span className={styles.errorText}>{errors.kassasysteem}</span>}
            </div>
            
            <div className={styles.fieldGroup}>
              <label htmlFor="kassaleverancier" className={styles.label}>
                {t.labels?.kassaleverancier || 'Kassaleverancier'} <span className={styles.optional}>({t.labels?.optional || 'optioneel'})</span>
              </label>
              <input
                type="text"
                id="kassaleverancier"
                value={formData.kassaleverancier || ''}
                onChange={(e) => onChange('kassaleverancier', e.target.value)}
                className={`${styles.input} ${errors.kassaleverancier ? styles.inputError : ''}`}
                placeholder={t.placeholders?.kassaleverancier || 'Bijv. Konacom'}
              />
              {errors.kassaleverancier && <span className={styles.errorText}>{errors.kassaleverancier}</span>}
            </div>
          </div>
        )}

        {/* Message - Full Width - Can be hidden */}
        {!hideMessage && (
          <div className={styles.fieldGroup}>
            <label htmlFor="message" className={styles.label}>
              {t.labels?.message || 'Bericht'} <span className={styles.optional}>({t.labels?.optional || 'optioneel'})</span>
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => onChange('message', e.target.value)}
              className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
              placeholder={t.placeholders?.message || 'Vertel ons over uw wensen en behoeften...'}
              rows={4}
            />
            {errors.message && <span className={styles.errorText}>{errors.message}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
